import React from 'react';
import { Box, Typography } from '@mui/material';
import Image from 'next/image';

import { FlowCta } from 'src/components/jump-page/flow-cta';

import { useBrandedLPAnalytics } from '../context';
import { EventType, SectionName } from 'src/utils/analytics/constants';
import { useImpressionEvent } from 'src/utils/analytics/hooks';

type CardProps = {
  src: string;
  step: number;
  headline: string;
  description: string;
};

const texts = Object.freeze({
  heading: 'How does it work?',
  ctaLabel: 'Get started',
  cardStep: 'step',
});

const Card = ({ src, step, headline, description }: CardProps) => {
  return (
    <Box maxWidth="408px" height="100%" flex={1}>
      <Box
        paddingTop={3.5}
        paddingBottom={4.75}
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="center"
        gap={1.5}
      >
        <Box
          minWidth={{ xs: '331px', xl: '408px' }}
          maxWidth={{ xs: '100%', xl: 500 }}
        >
          <Image
            src={src}
            alt=""
            style={{ width: '100%', height: 'auto' }}
            width={0}
            height={0}
            sizes="100vw"
            unoptimized
          />
        </Box>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          alignItems={{ xs: 'center', xl: 'flex-start' }}
          textAlign={{ xs: 'center', xl: 'left' }}
          justifyContent="center"
          gap={1.5}
        >
          <Typography
            fontSize="14px"
            lineHeight="21px"
            fontWeight="500"
            color="green.main"
            textTransform="uppercase"
          >
            {texts.cardStep}&nbsp;{step}
          </Typography>
          <Typography variant="textBold" fontSize={20}>
            {headline}
          </Typography>
          <Typography>{description}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const cards = Object.freeze([
  {
    imageUrl:
      'https://www.nerdwallet.com/cdn/cardfinder/how-does-it-work-1.webp',
    headline: 'Tell us what you’re looking for',
    description:
      'We’ll ask about your financial habits and goals to show you the right cards.',
  },
  {
    imageUrl:
      'https://www.nerdwallet.com/cdn/cardfinder/how-does-it-work-2.webp',
    headline: 'We’ll crunch the numbers',
    description:
      'CardFinder analyzes a wide variety of cards to find the ones that fit your goals.',
  },
  {
    imageUrl:
      'https://www.nerdwallet.com/cdn/cardfinder/how-does-it-work-3.webp',
    headline: 'Compare results and apply',
    description:
      'Use our objective ratings and card info to pick a great card and apply immediately.',
  },
]);

export const HowDoesItWorkSection = () => {
  const { navigationId, impressionId } = useBrandedLPAnalytics();

  const { ref } = useImpressionEvent({
    eventType: EventType.ElementImpression,
    navigationId: navigationId,
    payload: {
      entity_name: SectionName.JumpHowItWorks,
      section_name: SectionName.JumpHowItWorks,
      section_position: 1,
      parent_impression_id: impressionId,
    },
  });

  return (
    <Box
      ref={ref}
      paddingTop={11}
      paddingBottom={2}
      width="90%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={3}
      margin="0 auto"
    >
      <Typography
        fontWeight={500}
        textAlign="center"
        fontSize={24}
        lineHeight="36px"
      >
        {texts.heading}
      </Typography>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        gap={5}
      >
        {cards.map((card, index) => {
          return (
            <Card
              key={card.headline}
              src={card.imageUrl}
              step={index + 1}
              headline={card.headline}
              description={card.description}
            />
          );
        })}
      </Box>
      <FlowCta
        trkLocation={SectionName.JumpHowItWorks}
        sx={{
          borderRadius: 1,
          height: '44px',
          marginTop: 1,
          paddingRight: 3,
          maxWidth: '408px',
        }}
      >
        <Typography fontSize={14} variant="accentSmall">
          {texts.ctaLabel}
        </Typography>
      </FlowCta>
    </Box>
  );
};
